import { numeralsOf } from "@/lib/numbering";
import {
  drawnOn,
  titleMatches,
  type Definitions,
} from "@/lib/element-registry";
import type {
  ReferenceChoice,
  ReferenceMark,
  ReferenceSource,
} from "@/components/ui/StructuredText";

/** The letters a phrase needs before the list opens, as the shared type-ahead's rule. */
const FEWEST_LETTERS = 2;

/**
 * The elements a claim's words can name, and the numeral each one shows.
 *
 * The matching is `titleMatches`, the same read the drawing pane offers from, so
 * one list can never offer an element the other does not. A claim asks for
 * `exact` because there the fully typed title is the answer being waited for.
 *
 * The numeral is read through `numeralsOf`, the same projection the drawings are
 * numbered by, so a claim and a drawing can never show one element two numbers.
 *
 * Every element in the registry shows its numeral, as v0.28 shows them (R272).
 * While no drawing carries a pin, each is provisional and says it comes from the
 * registry; once one does, an element on no drawing is marked as not yet on one,
 * and a drawn element opens on its drawing through ``open``. A group is drawn when
 * one of its members is, since its members are what the sheets pin.
 */
export function claimReferences(
  definitions: Definitions | undefined,
  open?: (elementId: string) => void,
): ReferenceSource {
  if (definitions === undefined)
    return { suggest: () => [], tagOf: () => null, markOf: () => null };
  const numerals = numeralsOf(definitions);
  // Both read once for the whole registry. A claim asks which drawings carry an
  // element on every keystroke and again for every drawn word it renders, and
  // asking figure by figure each time is that walk over for every answer.
  const on = drawnOn(definitions);
  const titles = new Map(
    definitions.registry.map((row) => [row.element_id, row.title] as const),
  );
  const drawnMembers = new Set(
    definitions.registry
      .filter((row) => row.group_id && on.has(row.element_id))
      .map((row) => row.group_id),
  );
  const isDrawn = (elementId: string) =>
    on.has(elementId) || drawnMembers.has(elementId);
  /** The numeral an element shows in the type-ahead, which it has only once it is drawn. */
  const drawnAs = (elementId: string): string | undefined =>
    isDrawn(elementId) ? numerals.get(elementId) : undefined;
  const anyPinned = on.size > 0;
  const markOf = (elementId: string): ReferenceMark | null => {
    const numeral = numerals.get(elementId);
    if (numeral === undefined) return null;
    const title = titles.get(elementId) ?? "";
    if (!anyPinned)
      return {
        title: `Reference numeral ${numeral} \u00b7 ${title} (from the element registry; the figures are not yet numbered)`,
        unconfirmed: false,
      };
    if (!isDrawn(elementId))
      return {
        title: `Reference numeral ${numeral} is not yet numbered on a drawing`,
        unconfirmed: true,
      };
    return {
      title: `Reference numeral ${numeral} \u00b7 ${title} \u00b7 click to see it on the drawing`,
      unconfirmed: false,
      ...(open === undefined ? {} : { open: () => open(elementId) }),
    };
  };
  return {
    suggest: (phrase) =>
      phrase.trim().length < FEWEST_LETTERS
        ? []
        : // Every match is the start of a title, so among them the drawn ones lead.
          titleMatches(definitions, phrase, {
            exact: true,
            leading: (elementId) => drawnAs(elementId) !== undefined,
          }).map((hit): ReferenceChoice => {
            const numeral = drawnAs(hit.elementId);
            return numeral === undefined
              ? {
                  reference: hit.elementId,
                  label: hit.title,
                  detail: "not yet on a drawing",
                }
              : {
                  reference: hit.elementId,
                  label: hit.title,
                  numeral,
                };
          }),
    tagOf: (reference) => {
      const numeral = numerals.get(reference);
      return numeral === undefined ? null : `(${numeral})`;
    },
    markOf,
  };
}
